// ============================================================================
// src/lib/sessionProfileApi.ts — the session's privilege-source profile read
// ============================================================================
// One function, one PostgREST chain: the `profiles` row App's `loadUserImpl`
// fetches to learn WHO is signed in and WHAT ROLE they hold. Ask 3 of the
// engineer spec ("no direct db call in a .tsx component") is satisfied by
// moving the transport into the data layer, which is where a supabase call
// belongs — not by hiding the decision that surrounds it.
//
// WHAT DELIBERATELY DID NOT MOVE, AND WHY IT MUST NOT
//
// The decisions stay in App; only the mechanics were ever allowed to leave:
//
//   * the FAIL-CLOSED CATCH: `role: 'inactive'`, `setView('home')`, and the
//     two readiness setters that stop the pig record page loading forever;
//   * the `throw` on a failed outcome — the decision to treat a failed fetch
//     as a failure rather than as an empty profile.
//
// The retry loop, the ONE overall 20-second resolution budget, the abort
// composition and the lease checks now live in
// src/app/boot/sessionProfileLoad.ts. There is no per-attempt deadline: a
// single budget bounds the whole resolution, a genuine timeout ends it, and
// only fast transient failures retry within what remains. That module decides NOTHING about
// privilege: it returns `resolved` / `failed` / `superseded` and never invents
// a row, a role, or a default, so App still holds every literal the guard below
// reads. It replaced an un-aborted 5-second promise race that left each
// timed-out attempt in flight against PostgREST — see that module's header for
// the deadline and its trade-off.
//
// That last one is not a style preference. A profile failure used to set
// `role: 'admin'`, elevating every signed-in user to client-side admin on any
// transient error, and tests/static/security_client_access_static.test.js
// exists to make that regression impossible. It reads loadUser's own braced
// region out of src/App and asserts the catch never names a privileged role.
// A guard that reads source text can only see code that is still in the file
// it reads, so moving the catch — or the role literals, or the readiness
// calls — behind a lib function would leave the guard passing over an
// unguarded App. On 2026-08-18 that exact collapse was demonstrated: with the
// region silently empty, the test named "the catch path NEVER sets role:admin"
// passed over a client that elevated every signed-in user. Only the TRANSPORT
// is here. The privilege decision stays where the guard can see it.
//
// WHY THIS RETURNS AN UNAWAITED REQUEST
//
// `readSessionProfile` is NOT `async`. It composes the chain and hands the
// caller the PostgREST request object without subscribing to it, because
// supabase-js builders are lazy — the fetch is issued by `then()`, not by
// construction. The caller arms the ONE 20-second resolution budget and this
// attempt's own abort controller before it subscribes, so awaiting here would
// start the request ahead of the machinery that bounds it. Returning the
// unsubscribed request keeps construction and subscription in the order the
// caller established.
//
// ONE RETRY OWNER, AND IT IS NOT THIS FILE
//
// @supabase/postgrest-js 2.103.3 retries by DEFAULT: `retryEnabled =
// builder.retry ?? true`, with DEFAULT_MAX_RETRIES = 3, RETRYABLE_STATUS_CODES
// = [520, 503], RETRYABLE_METHODS = ['GET','HEAD','OPTIONS'], plus network
// errors on idempotent methods — backing off 1s, 2s, 4s.
//
// A `.single()` GET is idempotent, so an unmodified builder answers a 503 with
// FOUR physical requests over about seven seconds. The caller then repeats the
// whole builder up to three times, so 'three attempts' silently meant up to
// twelve requests and more backoff than the caller's entire 20-second budget.
// Two independent retry policies were stacked, and only one of them was
// visible to the code that decides what a failure means.
//
// `.retry(false)` makes this request exactly one round trip. Retrying is then
// wholly owned by src/app/boot/sessionProfileLoad.ts, which is where the
// budget, the classifier and the abort live. Applied HERE, to this one query,
// and deliberately not to the shared client: every other read keeps the
// library default, because no other read has a caller-side retry loop.
//
// WHY NOT userDirectoryApi, AND WHY NOT A ROUTE RESOURCE
//
// src/lib/userDirectoryApi.ts also reads `profiles`, and this is deliberately
// not a second function in it. That module answers "who are all the users" for
// an admin screen, throws a prefixed Error, and treats `[]` as data. This one
// answers "who am I" for the boot's privilege gate, returns the raw
// `{data, error}` pair so the caller can decide, and expects exactly one row.
// Sharing a file would invite sharing a contract, and the contracts are
// opposites.
//
// It is not a routeDataCatalog resource for the same reason: `profiles` has
// zero readers in the read catalog on purpose. This read runs during session
// bootstrap, before any route owns anything, fenced by a boot lease rather
// than by Query's cache.
// ============================================================================

import type {SupabaseClient} from './supabaseTypes.js';

// The abort application, kept BESIDE the chain it wraps rather than left
// at the call site, so the whole request — projection, filter, abort plumbing
// and single-row expectation — is one readable unit.
//
// Generic in the builder so the PostgREST response type survives the wrapper
// and the caller's `res.error` / `res.data` stay checked. The duck-typed probe
// is not defensive noise: injected and faked builders in tests do not all
// expose `abortSignal`, and the caller's lease check remains mandatory either
// way. This mirrors App's own `withLeaseAbortSignal`, which still wraps the
// legacy boot's app_store read.
function withLeaseSignal<Q>(query: Q, signal: AbortSignal | null): Q {
  if (signal && query && typeof (query as {abortSignal?: unknown}).abortSignal === 'function')
    return (query as unknown as {abortSignal: (s: AbortSignal) => Q}).abortSignal(signal);
  return query;
}

/**
 * The signed-in user's own `profiles` row, as an UNSUBSCRIBED PostgREST
 * request — see the header: the caller arms the resolution budget and this
 * attempt's abort controller before it subscribes, so the fetch must not start
 * at construction.
 *
 * `signal` is the ATTEMPT's signal, not the boot lease's directly. The caller
 * gives each attempt its own controller purely to CANCEL it — the deadline is
 * a single 20-second budget across the whole resolution, raced separately,
 * because a request blocked before it reaches `fetch` would never observe an
 * abort at all. This function does not care which mechanism ended it.
 *
 * Resolves to the raw `{data, error}` pair. It does NOT throw on a PostgREST
 * error and it does NOT substitute a default row, because the caller's whole
 * reason to exist is deciding what a missing profile means, and here that
 * decision is "fail closed to role:inactive".
 *
 * `user` is passed whole rather than as an id string so the filter reads the
 * same as it did in App and the call-site inventory can prove this is the same
 * physical read at a new address rather than a reimplementation of it:
 * tests/fixtures/render-read-sites.json records the SAME fingerprint
 * (#30aa1f73) on both sides of the move.
 */
export function readSessionProfile(sb: SupabaseClient, user: {id: string}, signal: AbortSignal | null) {
  const profileQuery = sb.from('profiles').select('*').eq('id', user.id);
  // `.retry(false)` — SEE THE HEADER. postgrest-js retries GETs by default;
  // this read must issue exactly one physical request per attempt so that
  // src/app/boot/sessionProfileLoad.ts is the only thing counting attempts.
  return withLeaseSignal(profileQuery, signal).single().retry(false);
}
